package dev.lingvo.shared.screenshots.templates

import dev.lingvo.shared.DeviceClass
import dev.lingvo.shared.screenshots.doc.BackgroundLayer
import dev.lingvo.shared.screenshots.doc.Blob
import dev.lingvo.shared.screenshots.doc.Board
import dev.lingvo.shared.screenshots.doc.Box
import dev.lingvo.shared.screenshots.doc.BrandKit
import dev.lingvo.shared.screenshots.doc.DeviceLayer
import dev.lingvo.shared.screenshots.doc.FitSpec
import dev.lingvo.shared.screenshots.doc.FrameFinish
import dev.lingvo.shared.screenshots.doc.Grain
import dev.lingvo.shared.screenshots.doc.LayerBase
import dev.lingvo.shared.screenshots.doc.LayerPatch
import dev.lingvo.shared.screenshots.doc.LocaleOverride
import dev.lingvo.shared.screenshots.doc.Page
import dev.lingvo.shared.screenshots.doc.Palette
import dev.lingvo.shared.screenshots.doc.Paint
import dev.lingvo.shared.screenshots.doc.Role
import dev.lingvo.shared.screenshots.doc.SceneKind
import dev.lingvo.shared.screenshots.doc.SceneSpec
import dev.lingvo.shared.screenshots.doc.Shadow
import dev.lingvo.shared.screenshots.doc.ShapeKind
import dev.lingvo.shared.screenshots.doc.ShapeLayer
import dev.lingvo.shared.screenshots.doc.Stop
import dev.lingvo.shared.screenshots.doc.StrokeSpec
import dev.lingvo.shared.screenshots.doc.TemplateId
import dev.lingvo.shared.screenshots.doc.TextAlign
import dev.lingvo.shared.screenshots.doc.TextContent
import dev.lingvo.shared.screenshots.doc.TextLayer
import dev.lingvo.shared.screenshots.doc.TextStyle
import dev.lingvo.shared.screenshots.doc.TextTransform
import dev.lingvo.shared.screenshots.doc.TypeRole
import dev.lingvo.shared.screenshots.geom.safeArea
import dev.lingvo.shared.screenshots.scene.SceneInput
import dev.lingvo.shared.screenshots.scene.Scenes

internal class StyledTemplate(
    id: String, override val displayName: String, category: TemplateCategory, private val family: String,
    private val weight: Int, private val max: Double, private val floor: Double, private val leading: Double,
    private val tracking: Double, private val finish: FrameFinish, private val scene: SceneSpec = SceneSpec(SceneKind.SINGLE),
    private val captionAlign: TextAlign? = null,
) : Template {
    override val id = TemplateId(id)
    override val categories = setOf(category)
    override val defaultHue = 145.0
    override val families = mapOf(TypeRole.DISPLAY to family, TypeRole.TEXT to "Inter", TypeRole.MONO to "IBM Plex Mono", TypeRole.SERIF to if (id == "paper") "Fraunces" else "Instrument Serif")
    override val mirrorRtl = id != "panorama"

    override fun build(brand: BrandKit, board: Board, slots: List<TemplateSlot>, palette: Palette): List<Page> {
        require(slots.isNotEmpty() && slots.size <= 10) { "Templates need the selected plan slots" }
        if (board.deviceClass == DeviceClass.PLAY_FEATURE_GRAPHIC.id) return featureGraphicPages(brand, board, slots, family)
        val frame = templateFrame(board)
        val baseScene = if (scene.kind == SceneKind.PANORAMA) SceneSpec(SceneKind.SINGLE) else scene
        val pages = slots.mapIndexed { index, slot ->
            val page = buildPage(board, slot, index, baseScene, frame, family)
            decorate(page, board, brand, index)
        }
        if (scene.kind != SceneKind.PANORAMA) return pages.map(::naturalCjkTracking)
        val panorama = Scenes.toPanorama(pages, board.canvas)
        return listOf(naturalCjkTracking(Scenes.apply(panorama, SceneInput(board.canvas, slots.size, safeArea(board, defaultFrame(board)), scene, frame,
            board.canvas.height.toDouble() / board.canvas.width))))
    }

    // Latin recipe tracking must not tighten translated CJK captions; numeric eyebrows retain their own style.
    private fun naturalCjkTracking(page: Page): Page {
        val patches = page.layers.filterIsInstance<TextLayer>()
            .filter { it.content is TextContent.SlotCaption && it.style.letterSpacingEm != 0.0 }
            .associate { it.base.id to LayerPatch(letterSpacingEm = 0.0) }
        if (patches.isEmpty()) return page
        val overrides = page.overrides.toMutableMap()
        for (locale in listOf("ja", "zh", "ko")) {
            val previous = overrides[locale] ?: LocaleOverride()
            overrides[locale] = previous.copy(patches = previous.patches + patches)
        }
        return page.copy(overrides = overrides)
    }

    private fun decorate(page: Page, board: Board, brand: BrandKit, index: Int): Page {
        val w = board.canvas.width.toDouble()
        val h = board.canvas.height.toDouble()
        val name = id.id
        val layers = page.layers.map { layer -> when (layer) {
            is BackgroundLayer -> when (name) {
                "aurora" -> layer.copy(blobs = listOf(Blob(.15, .12, .55, "@accent/0.55"), Blob(.85, .35, .50, "@surface/0.45"), Blob(.40, .90, .60, "@slot/0.35")))
                "paper" -> layer.copy(grain = Grain(42, .035))
                "pop" -> layer.copy(fill = Paint.Solid("@slot"))
                "panorama" -> layer.copy(fill = Paint.Linear(90.0, listOf(Stop(0.0, "@bg"), Stop(.5, "@surface"), Stop(1.0, "@accent"))))
                else -> layer
            }
            is DeviceLayer -> layer.copy(finish = finish, base = layer.base.copy(
                box = if (name == "split") {
                    if (w <= h) layer.base.box.copy(y = .30 * h) else layer.base.box.copy(x = .40 * w)
                } else layer.base.box,
                shadow = if (name == "paper") Shadow(0.0, .02 * w, (.06 * w).coerceAtMost(400.0), "@ink/0.18") else layer.base.shadow))
            is TextLayer -> layer.copy(style = layer.style.copy(family = family, weight = weight, lineHeight = leading,
                role = if (name in listOf("paper", "editorial")) TypeRole.SERIF else TypeRole.DISPLAY,
                letterSpacingEm = tracking, align = captionAlign ?: if (name == "pop") TextAlign.CENTER else TextAlign.START),
                fit = FitSpec(halfPixel(max * w), halfPixel(floor * w), 3))
            else -> layer
        } }.toMutableList()
        if (name == "night") {
            val deviceBox = layers.filterIsInstance<DeviceLayer>().first().base.box
            val glowBox = Box(deviceBox.x + deviceBox.w / 2 - .7 * w, deviceBox.y + deviceBox.h * 2 / 3 - .7 * w, 1.4 * w, 1.4 * w)
            layers.add(1, ShapeLayer(LayerBase("${page.id}-glow", box = glowBox), ShapeKind.ELLIPSE,
                fill = Paint.Radial(.5, .5, .5, listOf(Stop(0.0, "@surface/0.5"), Stop(1.0, "@surface/0")))))
            val captionIndex = layers.indexOfFirst { it.base.role == Role.CAPTION }
            val caption = layers[captionIndex] as TextLayer
            layers[captionIndex] = caption.copy(base = caption.base.copy(box = caption.base.box.copy(h = .70 * caption.base.box.h)))
            layers += TextLayer(LayerBase("${page.id}-subcaption", box = caption.base.box.copy(y = caption.base.box.y + caption.base.box.h * .78, h = .06 * h), role = Role.SUBCAPTION),
                TextContent.Literal(brand.name), TextStyle(role = TypeRole.TEXT, family = "Inter", weight = 400, color = Paint.Solid("@inkMuted")), FitSpec(halfPixel(.032 * w), halfPixel(.024 * w), 2))
        }
        if (name == "split") layers.add(1, ShapeLayer(LayerBase("${page.id}-band", box = if (w <= h) Box(0.0, 0.0, w, .40 * h) else Box(0.0, 0.0, .45 * w, h), role = Role.BAND), ShapeKind.RECT, fill = Paint.Solid("@accent")))
        if (name in listOf("mono", "editorial")) {
            val safe = safeArea(board, defaultFrame(board))
            val captionIndex = layers.indexOfFirst { it.base.role == Role.CAPTION }
            val caption = layers[captionIndex] as TextLayer
            layers[captionIndex] = caption.copy(base = caption.base.copy(box = caption.base.box.copy(y = caption.base.box.y + .05 * w, h = (caption.base.box.h - .05 * w).coerceAtLeast(1.0))))
            layers += ShapeLayer(LayerBase("${page.id}-number-field", box = Box(caption.base.box.x, caption.base.box.y.coerceAtLeast(safe.y), .065 * w, .04 * w)),
                ShapeKind.RECT, fill = Paint.Solid("@accentInk"))
            layers += TextLayer(LayerBase("${page.id}-eyebrow", box = Box(caption.base.box.x, caption.base.box.y.coerceAtLeast(safe.y), caption.base.box.w, .04 * w), role = Role.EYEBROW),
                TextContent.Literal((index + 1).toString().padStart(2, '0')), TextStyle(role = TypeRole.MONO, family = "IBM Plex Mono", weight = 500,
                    color = Paint.Solid("@accent"), letterSpacingEm = .08, transform = TextTransform.UPPERCASE), FitSpec(halfPixel(.028 * w), halfPixel(.028 * w), 1))
            if (name == "editorial") layers += ShapeLayer(LayerBase("${page.id}-rule", box = Box(caption.base.box.x, safe.bottom - .015 * h, caption.base.box.w, (.002 * w).coerceAtLeast(1.0))),
                ShapeKind.LINE, stroke = StrokeSpec(.002 * w, Paint.Solid("@ink/0.5")))
        }
        return page.copy(layers = layers)
    }
}
